import { GripVerticalIcon } from "lucide-react";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import type {
  ComponentProps,
  CSSProperties,
  KeyboardEvent,
  PointerEvent,
} from "react";

import { STORAGE_KEYS } from "@/shared/constants/storage-keys";
import { storage } from "@/shared/lib/storage";
import { cn } from "@/shared/lib/utils";
import { SidebarProvider, useSidebar } from "@/shared/ui/sidebar";

const DEFAULT_SIDEBAR_WIDTH = 256;
const MIN_SIDEBAR_WIDTH = 220;
const MAX_SIDEBAR_WIDTH = 400;
const KEYBOARD_RESIZE_STEP = 8;

interface SidebarResizeContextValue {
  isResizing: boolean;
  width: number;
  beginResize: () => void;
  resizeTo: (width: number) => void;
  finishResize: () => void;
}

const SidebarResizeContext = createContext<SidebarResizeContextValue | null>(
  null,
);

function clampSidebarWidth(width: number) {
  return Math.min(MAX_SIDEBAR_WIDTH, Math.max(MIN_SIDEBAR_WIDTH, width));
}

function getInitialSidebarWidth() {
  const storedWidth = storage.getItem<number>(STORAGE_KEYS.sidebarWidth);

  return typeof storedWidth === "number" && Number.isFinite(storedWidth)
    ? clampSidebarWidth(storedWidth)
    : DEFAULT_SIDEBAR_WIDTH;
}

export function ResizableSidebarProvider({
  className,
  style,
  ...props
}: ComponentProps<typeof SidebarProvider>) {
  const [width, setWidth] = useState(getInitialSidebarWidth);
  const [isResizing, setIsResizing] = useState(false);
  const widthRef = useRef(width);

  const resizeTo = useCallback((nextWidth: number) => {
    const clampedWidth = clampSidebarWidth(nextWidth);
    widthRef.current = clampedWidth;
    setWidth(clampedWidth);
  }, []);

  const finishResize = useCallback(() => {
    setIsResizing(false);
    storage.setItem(STORAGE_KEYS.sidebarWidth, widthRef.current);
  }, []);

  useEffect(() => {
    if (!isResizing) return;

    const previousCursor = document.body.style.cursor;
    const previousUserSelect = document.body.style.userSelect;
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";

    return () => {
      document.body.style.cursor = previousCursor;
      document.body.style.userSelect = previousUserSelect;
    };
  }, [isResizing]);

  return (
    <SidebarResizeContext.Provider
      value={{
        isResizing,
        width,
        beginResize: () => setIsResizing(true),
        resizeTo,
        finishResize,
      }}
    >
      <SidebarProvider
        className={cn(
          isResizing &&
            "[&_[data-slot=sidebar-container]]:transition-none [&_[data-slot=sidebar-gap]]:transition-none",
          className,
        )}
        style={
          {
            ...style,
            "--sidebar-width": `${width}px`,
          } as CSSProperties
        }
        {...props}
      />
    </SidebarResizeContext.Provider>
  );
}

function useSidebarResize() {
  const context = useContext(SidebarResizeContext);

  if (!context) {
    throw new Error(
      "SidebarResizeHandle must be used within ResizableSidebarProvider.",
    );
  }

  return context;
}

export function SidebarResizeHandle() {
  const { isMobile, state } = useSidebar();
  const {
    isResizing,
    width,
    beginResize,
    resizeTo,
    finishResize,
  } = useSidebarResize();

  if (isMobile || state === "collapsed") return null;

  const handlePointerDown = (event: PointerEvent<HTMLButtonElement>) => {
    if (event.button !== 0) return;

    event.preventDefault();
    event.currentTarget.setPointerCapture(event.pointerId);
    beginResize();
  };

  const handlePointerMove = (event: PointerEvent<HTMLButtonElement>) => {
    if (!event.currentTarget.hasPointerCapture(event.pointerId)) return;
    resizeTo(event.clientX);
  };

  const handlePointerEnd = (event: PointerEvent<HTMLButtonElement>) => {
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    finishResize();
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
    if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return;

    event.preventDefault();
    const nextWidth = clampSidebarWidth(
      width +
        (event.key === "ArrowRight" ? 1 : -1) * KEYBOARD_RESIZE_STEP,
    );
    resizeTo(nextWidth);
    storage.setItem(STORAGE_KEYS.sidebarWidth, nextWidth);
  };

  return (
    <button
      type="button"
      role="separator"
      aria-label="调整侧边栏宽度"
      aria-orientation="vertical"
      aria-valuemin={MIN_SIDEBAR_WIDTH}
      aria-valuemax={MAX_SIDEBAR_WIDTH}
      aria-valuenow={width}
      data-resizing={isResizing}
      className="group/resize absolute inset-y-0 -right-2 z-30 hidden w-4 cursor-col-resize touch-none select-none md:block"
      onKeyDown={handleKeyDown}
      onPointerCancel={handlePointerEnd}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerEnd}
    >
      <span className="bg-border absolute inset-y-0 left-1/2 w-px -translate-x-1/2 opacity-0 transition-opacity group-hover/resize:opacity-100 group-focus-visible/resize:opacity-100 group-data-[resizing=true]/resize:opacity-100" />
      <span className="bg-border pointer-events-none absolute top-1/2 left-1/2 z-10 flex h-4 w-3 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-xs border opacity-0 transition-opacity group-hover/resize:opacity-100 group-focus-visible/resize:opacity-100 group-data-[resizing=true]/resize:opacity-100">
        <GripVerticalIcon className="size-2.5" />
      </span>
    </button>
  );
}
